export interface ConvertedPage { image: Blob; width: number; height: number }
interface Job {
  blob: Blob; signal?: AbortSignal; abort: () => void; resolve: (page: ConvertedPage) => void; reject: (error: Error) => void
  worker?: Worker; timer?: ReturnType<typeof setTimeout>; done: boolean
}

export class PageConverter {
  private queued: Job[] = []
  private active = new Set<Job>()
  private disposed = false
  private pumping = false
  get activeCount(): number { return this.active.size }
  get queuedCount(): number { return this.queued.length }

  convert(blob: Blob, signal?: AbortSignal): Promise<ConvertedPage> {
    if (this.disposed || signal?.aborted) return Promise.reject(new DOMException('已取消轉檔', 'AbortError'))
    return new Promise((resolve, reject) => {
      const job: Job = { blob, signal, resolve, reject, done: false, abort: () => this.finish(job, new DOMException('已取消轉檔', 'AbortError')) }
      signal?.addEventListener('abort', job.abort, { once: true })
      this.queued.push(job)
      this.pump()
    })
  }
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    const jobs = [...this.queued, ...this.active]
    this.queued = []
    for (const job of jobs) job.abort()
  }
  private pump(): void {
    if (this.disposed || this.pumping) return
    this.pumping = true
    try {
      while (this.active.size < 2 && this.queued.length) {
        const job = this.queued.shift()!
        this.active.add(job)
        try {
          const worker = new Worker(new URL('../workers/page-converter.worker.ts', import.meta.url), { type: 'module' })
          job.worker = worker
          job.timer = setTimeout(() => this.finish(job, new Error('圖片轉換逾時，請重試')), 60000)
          worker.onerror = () => this.finish(job, new Error('轉檔 Worker 無法執行'))
          worker.onmessageerror = () => this.finish(job, new Error('無法取得轉檔結果'))
          worker.onmessage = (event: MessageEvent<ConvertedPage | { error: string }>) => {
            if ('error' in event.data) this.finish(job, new Error(event.data.error))
            else this.finish(job, undefined, event.data)
          }
          worker.postMessage(job.blob)
        } catch { this.finish(job, new Error('無法啟動轉檔 Worker')) }
      }
    } finally { this.pumping = false }
  }
  private finish(job: Job, error?: Error, result?: ConvertedPage): void {
    if (job.done) return
    job.done = true
    clearTimeout(job.timer)
    job.signal?.removeEventListener('abort', job.abort)
    job.worker?.terminate()
    this.active.delete(job)
    const index = this.queued.indexOf(job)
    if (index >= 0) this.queued.splice(index, 1)
    if (error) job.reject(error)
    else if (result) job.resolve(result)
    this.pump()
  }
}
